import { useCallback, useState } from 'react';
import type { CreativeColumnKey } from '../types/hypothesis';
import { DEFAULT_CREATIVE_COLUMNS } from '../types/hypothesis';

const STORAGE_KEY = 'fb_creative_column_config';

interface ColumnConfig {
  visible: CreativeColumnKey[];
  order: CreativeColumnKey[];
}

const getDefaultConfig = (): ColumnConfig => ({
  visible: [...DEFAULT_CREATIVE_COLUMNS],
  order: [...DEFAULT_CREATIVE_COLUMNS],
});

const loadConfig = (): ColumnConfig => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultConfig();

    const parsed = JSON.parse(raw) as ColumnConfig;
    // Filter out invalid keys for forward-compat
    const validVisible = parsed.visible.filter((k) =>
      DEFAULT_CREATIVE_COLUMNS.includes(k),
    );
    const validOrder = parsed.order.filter((k) =>
      DEFAULT_CREATIVE_COLUMNS.includes(k),
    );
    // Add any new columns that weren't in stored config
    for (const col of DEFAULT_CREATIVE_COLUMNS) {
      if (!validOrder.includes(col)) {
        validOrder.push(col);
        validVisible.push(col);
      }
    }

    return { visible: validVisible, order: validOrder };
  } catch {
    return getDefaultConfig();
  }
};

const saveConfig = (config: ColumnConfig) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
};

const useCreativeColumnVisibility = () => {
  const [config, setConfigState] = useState<ColumnConfig>(loadConfig);

  const setConfig = useCallback((next: ColumnConfig) => {
    setConfigState(next);
    saveConfig(next);
  }, []);

  const visibilityModel = config.order.reduce<Record<string, boolean>>(
    (acc, key) => {
      acc[key] = config.visible.includes(key);
      return acc;
    },
    {},
  );

  const toggleColumn = useCallback(
    (key: CreativeColumnKey) => {
      const next = { ...config };
      if (next.visible.includes(key)) {
        next.visible = next.visible.filter((k) => k !== key);
      } else {
        next.visible = [...next.visible, key];
      }
      setConfig(next);
    },
    [config, setConfig],
  );

  const moveColumn = useCallback(
    (key: CreativeColumnKey, direction: 'up' | 'down') => {
      const idx = config.order.indexOf(key);
      if (idx === -1) return;
      if (direction === 'up' && idx === 0) return;
      if (direction === 'down' && idx === config.order.length - 1) return;

      const next = [...config.order];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      setConfig({ ...config, order: next });
    },
    [config, setConfig],
  );

  const resetDefaults = useCallback(() => {
    setConfig(getDefaultConfig());
  }, [setConfig]);

  return {
    config,
    visibilityModel,
    columnOrder: config.order,
    toggleColumn,
    moveColumn,
    resetDefaults,
  };
};

export default useCreativeColumnVisibility;
